import {ViewportScroller} from '@angular/common';
import {ChangeDetectionStrategy, Component, ElementRef, OnInit, Renderer, ViewChild} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router, Scroll} from '@angular/router';
import { Angulartics2GoogleTagManager } from 'angulartics2/gtm';
import { filter } from 'rxjs/operators';

declare var $;
declare global {
	interface Window {
		dataLayer: any;
	}
}
window.dataLayer = window.dataLayer || [];

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.less'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
	@ViewChild('header', { static: false }) header: ElementRef;
	@ViewChild('main', { static: false }) main: ElementRef;
	@ViewChild('sidebarCollapse', { static: false }) sidebarCollapse: ElementRef;
	@ViewChild('primaryNav', { static: false }) primaryNav: ElementRef;

	constructor(
		private router: Router,
		private renderer: Renderer,
		private route: ActivatedRoute,
		private meta: Meta,
		private titleService: Title,
		private viewportScroller: ViewportScroller,
		angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
	) {
		angulartics2GoogleTagManager.startTracking();

		router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event) => {
			// Clear focus
			this.renderer.invokeElementMethod(this.header.nativeElement, 'focus');

			// Change page title
			const title = this.getTitleFromRoute(route.root);
			this.titleService.setTitle((!title ? '' : title + ' | ') + 'Hillsdale College K-12 At Home');

			// Change meta description
			const metaDescription = this.getMetaDescriptionFromRoute(route.root);
			if (metaDescription) this.meta.updateTag({ name: 'description', content: metaDescription });

			// Close navigation menu
			if (this.primaryNav.nativeElement.classList.contains('active')) {
				// console.log('Header Nav -- Closing primary nav on route change');
				this.sidebarCollapse.nativeElement.click();
			}
		});


		//https://stackoverflow.com/questions/44441089/angular4-scrolling-to-anchor
		//https://next.angular.io/api/router/ExtraOptions
		//https://www.npmjs.com/package/ngx-page-scroll
		let scrollTimeout = null;
		router.events.pipe(
			filter(e /*: e is Scroll*/ => e instanceof Scroll)
		).subscribe((e: Scroll) => {
			if (e.anchor) {
				// anchor navigation
				//if we get an anchor, but that anchor isn't on screen yet, try for up to three seconds to find it and scroll.
				if (!document.getElementById(e.anchor)) {
					//if we were waiting on a previous anchor, abandon that
					if (scrollTimeout) clearInterval(scrollTimeout);

					scrollTimeout = setInterval(() => {
						if (!document.getElementById(e.anchor)) return;
						viewportScroller.scrollToAnchor(e.anchor);
						clearInterval(scrollTimeout);
						scrollTimeout = null;
					}, 25);
					setTimeout(() => {
						if (!scrollTimeout) return;
						clearInterval(scrollTimeout);
					}, 3000);
				}
			}
		});
	}

	ngOnInit() {
	}

	skipLink() {
		this.renderer.invokeElementMethod(this.main.nativeElement, 'focus');
	}

	private getTitleFromRoute(route) {
		if (route.snapshot.data.hasOwnProperty('title')) {
			return route.snapshot.data.title;
		}

		const children: ActivatedRoute[] = route.children;

		// iterate over route children
		for (const child of children) {
			// verify that we have the correct router outlet
			if (child.outlet !== PRIMARY_OUTLET) {
				continue;
			}

			if ( ! child.snapshot.data.hasOwnProperty('title')) {
				return this.getTitleFromRoute(child);
			}

			return child.snapshot.data.title;
		}

		return false;
	}

	private getMetaDescriptionFromRoute(route) {
		if (route.snapshot.data.hasOwnProperty('description')) {
			return route.snapshot.data.description;
		}

		const children: ActivatedRoute[] = route.children;

		// iterate over route children
		for (const child of children) {
			// verify that we have the correct router outlet
			if (child.outlet !== PRIMARY_OUTLET) {
				continue;
			}

			if ( !child.snapshot.data.hasOwnProperty('description')) {
				return this.getMetaDescriptionFromRoute(child);
			}

			return child.snapshot.data.description;
		}

		return false;
	}
}
